import logoImage from '../assets/logo.png';
import cachetImage from '../assets/cachet.png';
import { formatNhNumeroForPrint } from '../utils/nhNumero';

/** Impression facture NH — document HTML autonome (identique à l'écran, sans Header NH). */

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatInvoiceDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fr-FR');
}

/** Date et heure d'impression / export PDF (ex. 31/03/2026 11:48). */
export function formatPrintDateTime(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  const datePart = d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/** Harmonise les champs client (NH stocke parfois codeTva au lieu de matriculeFiscale). */
export function normalizeInvoiceClientData(raw = {}) {
  const adresse =
    raw.adresse ??
    raw.client_adresse ??
    raw.clientAdresse ??
    raw.address ??
    '';
  const email = raw.email ?? raw.client_email ?? raw.clientEmail ?? '';
  return {
    societe: String(raw.societe ?? '').trim(),
    adresse: String(adresse).trim(),
    matriculeFiscale: String(
      raw.matriculeFiscale ?? raw.codeTva ?? raw.matricule_fiscal ?? raw.matricule ?? ''
    ).trim(),
    email: String(email).trim(),
  };
}

/** Clé de recherche tolérante (accents, espaces, symboles). */
function normalizeMatchKey(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function findClientInRegistry(note, societies) {
  if (!Array.isArray(societies) || societies.length === 0) return null;

  const clientId = note?.clientId ?? note?.client_id;
  if (clientId != null && clientId !== '') {
    const byId = societies.find((s) => Number(s.id) === Number(clientId));
    if (byId) return byId;
  }

  const base = normalizeInvoiceClientData(note);
  const socKey = normalizeMatchKey(base.societe);
  const mfKey = normalizeMatchKey(base.matriculeFiscale);

  if (!socKey && !mfKey) return null;

  return (
    societies.find((s) => {
      const nom = normalizeMatchKey(s.nom ?? s.societe);
      const mat = normalizeMatchKey(
        s.matricule ?? s.matriculeFiscale ?? s.matricule_fiscal ?? s.codeTva
      );
      if (socKey && nom && nom === socKey) return true;
      if (mfKey && mat && mat === mfKey) return true;
      if (socKey && nom && (nom.includes(socKey) || socKey.includes(nom))) return true;
      return false;
    }) || null
  );
}

/** Format client API → registre pour enrichissement facture. */
export function mapApiClientToRegistry(client) {
  if (!client || typeof client !== 'object') return null;
  return {
    id: client.id,
    nom: client.nom,
    matricule: client.matricule_fiscal ?? client.matricule ?? '',
    adresse: client.adresse ?? '',
    email: client.email ?? '',
  };
}

export function buildClientRegistryFromApiClients(clients = []) {
  return (clients || []).map(mapApiClientToRegistry).filter(Boolean);
}

/**
 * Complète adresse / e-mail / MF depuis la liste clients NH
 * (notes anciennes en localStorage souvent sans adresse).
 */
export function enrichInvoiceClientFromRegistry(note, societies = []) {
  const base = normalizeInvoiceClientData(note);
  const match = findClientInRegistry(note, societies);

  if (!match) {
    return { ...note, ...base };
  }

  const adresse =
    base.adresse ||
    String(match.adresse ?? '').trim();
  const email =
    base.email ||
    String(match.email ?? '').trim();
  const matriculeFiscale =
    base.matriculeFiscale ||
    String(
      match.matricule ?? match.matriculeFiscale ?? match.matricule_fiscal ?? match.codeTva ?? ''
    ).trim();

  return {
    ...note,
    ...base,
    clientId: note?.clientId ?? note?.client_id ?? match.id,
    adresse,
    email,
    matriculeFiscale,
  };
}

/** Enrichissement asynchrone (API) si le registre local ne suffit pas. */
export async function enrichInvoiceClientFromApi(note, localRegistry = []) {
  let enriched = enrichInvoiceClientFromRegistry(note, localRegistry);
  const afterLocal = normalizeInvoiceClientData(enriched);
  if (afterLocal.adresse) {
    return enriched;
  }

  const clientId = enriched.clientId ?? enriched.client_id;
  if (clientId != null && clientId !== '') {
    try {
      const res = await fetch(`/api/clients/${clientId}`);
      const data = await res.json();
      if (data?.success && data.client) {
        enriched = enrichInvoiceClientFromRegistry(enriched, [
          mapApiClientToRegistry(data.client),
        ]);
        if (normalizeInvoiceClientData(enriched).adresse) {
          return enriched;
        }
      }
    } catch (e) {
      console.warn('Facture : enrichissement client par id', e);
    }
  }

  try {
    const res = await fetch('/api/clients');
    const data = await res.json();
    if (data?.success && Array.isArray(data.clients)) {
      enriched = enrichInvoiceClientFromRegistry(
        enriched,
        buildClientRegistryFromApiClients(data.clients)
      );
    }
  } catch (e) {
    console.warn('Facture : enrichissement client liste complète', e);
  }

  return enriched;
}

const INVOICE_PRINT_STYLES = `
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    font-family: 'Segoe UI', Arial, sans-serif;
    color: #333;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .invoice-paper {
    background: #fff;
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 28px 32px;
    position: relative;
    page-break-inside: avoid;
  }
  .invoice-print-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    font-size: 11px;
    color: #333;
    margin-bottom: 10px;
    line-height: 1.3;
  }
  .invoice-print-banner-left {
    text-align: left;
    flex: 1;
  }
  .invoice-print-banner-right {
    display: none;
  }
  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }
  .cabinet-logo {
    flex: 0 1 auto;
    text-align: left;
  }
  .logo-placeholder-invoice {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 0;
  }
  .header-logo-img {
    max-width: 190px;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0;
  }
  .cabinet-details {
    font-size: 12px;
    margin-top: 28px;
    line-height: 1.45;
    text-align: left;
  }
  .cabinet-details div {
    text-align: left;
  }
  .cabinet-name {
    text-align: right;
    color: #007bff;
    flex: 1;
    max-width: 52%;
    padding-top: 10px;
  }
  .cabinet-name h1 {
    margin: 0 0 4px;
    padding-bottom: 0;
    font-size: 24px;
    font-weight: normal;
    text-transform: none;
    text-align: right;
    border-bottom: none;
  }
  .subtitle {
    font-size: 12px;
    color: #666;
    margin-top: 28px;
    margin-bottom: 0;
    text-transform: none;
  }
  .contact-info {
    color: #333;
    font-size: 13px;
    margin-top: 2px;
    line-height: 1.6;
    text-transform: none;
  }
  .blue-divider {
    height: 2px;
    background-color: #007bff;
    margin: 18px 0;
  }
  .client-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 18px;
    gap: 20px;
  }
  .client-details {
    text-align: left;
    flex: 1;
  }
  .client-details h2 {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 700 !important;
    text-transform: none;
    text-align: left;
    line-height: 1.25;
  }
  .client-line {
    display: block;
    margin: 3px 0;
    line-height: 1.45;
    font-size: 13px;
    text-align: left;
    width: 100%;
  }
  .client-line.blue-text {
    text-align: left;
  }
  a.email-link {
    color: #007bff;
    text-decoration: none;
    text-transform: lowercase;
    font-family: inherit;
    display: inline;
    text-align: left;
  }
  .client-details .convention {
    margin: 8px 0 0;
    font-size: 13px;
  }
  .blue-text { color: #007bff; }
  .invoice-meta {
    text-align: right;
    flex-shrink: 0;
    min-width: 0;
    max-width: 58%;
    overflow: visible;
  }
  .invoice-number {
    color: #007bff;
    font-size: 20px;
    margin: 0 0 8px;
    font-weight: normal;
    text-transform: none;
    overflow: visible;
    white-space: normal;
    word-break: break-word;
  }

  .invoice-meta h2.invoice-number,
  h1, h2.invoice-number {
    font-weight: normal !important;
  }
  .invoice-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
    page-break-inside: avoid;
  }
  .invoice-table th {
    background-color: #edecec;
    padding: 10px 8px;
    text-align: left;
    vertical-align: middle;
    border-bottom: 2px solid #ddd;
    font-size: 11px;
    font-weight: normal !important;
    color: #313131;
    line-height: 1.35;
  }
  .invoice-table th:nth-child(3),
  .invoice-table th:nth-child(4),
  .invoice-table th:nth-child(5) {
    text-align: right;
  }
  .invoice-table td {
    padding: 10px 8px;
    border-bottom: 1px solid #eee;
    font-size: 11px;
    font-weight: normal !important;
    vertical-align: middle;
    line-height: 1.35;
  }
  .row-id {
    background-color: #007bff;
    color: #fff;
    width: 40px;
    text-align: center;
    vertical-align: middle;
    font-size: 11px;
    font-weight: normal !important;
  }
  .designation {
    background-color: #edecec;
  }
  .bg-light {
    background-color: #edecec;
    text-align: right;
  }
  .cell-tva {
    background-color: #cfcfcf;
    text-align: right;
  }
  .bg-blue-highlight {
    background-color: #007bff;
    color: #fff;
    text-align: right;
  }
  .totals-section {
    display: flex;
    justify-content: flex-end;
  }
  .totals-grid {
    display: grid;
    grid-template-columns: 220px 175px;
    align-items: stretch;
    text-align: right;
    line-height: 1.35;
    width: fit-content;
    margin-left: auto;
    page-break-inside: avoid;
  }
  .totals-grid .total-label,
  .totals-grid .total-value {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    box-sizing: border-box;
    min-height: 34px;
    border-bottom: 1px solid #c5c5c5;
    padding: 8px 10px;
    font-size: 14px;
    font-weight: normal !important;
  }
  .totals-grid .total-label {
    padding-right: 24px;
    text-align: right;
  }
  .totals-grid .total-value {
    text-align: right;
    padding-right: 12px;
  }
  .totals-grid .rs-line {
    border-bottom: none;
  }
  .totals-grid .border-top {
    border-bottom: none;
    border-top: 1px solid #007bff;
    padding-top: 12px;
    margin-top: 6px;
    color: #007bff;
  }
  .totals-grid .large,
  .totals-grid .border-top {
    font-size: 16px;
    font-weight: normal !important;
  }
  .thanks {
    margin: 14px 0 8px;
    font-size: 20px;
    page-break-after: avoid;
  }
  .bottom-note-container {
    background-color: #eef6ff;
    border-left: 5px solid #007bff;
    padding: 8px 12px;
    margin-top: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    page-break-inside: avoid;
  }
  .amount-in-words {
    flex: 1;
    text-transform: none;
  }
  .cachet-img {
    width: 170px;
    height: auto;
    object-fit: contain;
    display: block;
  }
  .footer-line {
    text-align: center;
    font-size: 10px;
    color: #6e6e6e;
    border-top: 1px solid #000;
    padding-top: 6px;
    margin-top: 10px;
    text-transform: none;
    page-break-before: avoid;
  }
  .invoice-footer-block {
    page-break-inside: avoid;
  }
  a.email-link {
    color: #0066cc;
    text-decoration: none;
    text-transform: lowercase;
  }
  @page { size: A4 portrait; margin: 8mm; }
  @media print {
    html, body {
      margin: 0;
      padding: 0;
      height: auto;
      overflow: visible;
    }
    .invoice-paper {
      padding: 0;
      max-width: 100%;
      width: 100%;
      box-shadow: none;
    }
    .invoice-paper.invoice-theme-cgm2 {
      padding-left: 14px;
    }
    .thanks { margin-top: 10px; }
    .bottom-note-container { margin-top: 6px; }
    .footer-line { margin-top: 8px; }
  }

  /* ========== THÈME CGM2 (orange-gris transparent) — couleurs pour print/PDF ========== */
  .invoice-paper.invoice-theme-cgm2 {
    font-family: Calibri, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
    color: #1f2937;
    background: rgba(245, 242, 239, 0.65) !important;
    border: 1px solid rgba(140, 120, 105, 0.2);
    padding-left: 40px;
    position: relative;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .invoice-paper.invoice-theme-cgm2,
  .invoice-paper.invoice-theme-cgm2 * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .invoice-paper.invoice-theme-cgm2::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: rgba(150, 120, 100, 0.32);
  }
  .invoice-theme-cgm2 .header-logo-img {
    max-width: 270px;
    height: auto;
    object-fit: contain;
  }
  .invoice-theme-cgm2 .cabinet-name { color: #9a7a66; max-width: 48%; padding-top: 4px; }
  .invoice-theme-cgm2 .cabinet-name h1 {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 18px;
    font-weight: 700 !important;
    letter-spacing: 0.02em;
    text-transform: none;
    color: #5c4f45;
    line-height: 1.3;
  }
  .invoice-theme-cgm2 .subtitle {
    margin-top: 6px;
    font-size: 10.5px;
    font-style: normal;
    font-weight: 400;
    color: #6b7280;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
  }
  .invoice-theme-cgm2 .contact-info,
  .invoice-theme-cgm2 .cabinet-details {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 11px;
    font-weight: 400;
    color: #4b5563;
    margin-top: 8px;
  }
  .invoice-theme-cgm2 .blue-divider {
    height: 0;
    margin: 12px 0 14px;
    background: transparent;
    border-top: 1.5px solid rgba(150, 120, 100, 0.28);
    border-bottom: none;
    padding-bottom: 0;
  }
  .invoice-theme-cgm2 .client-details h2 {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 17px;
    font-weight: 700 !important;
    letter-spacing: 0.01em;
    color: #5c4f45;
    text-transform: none;
  }
  .invoice-theme-cgm2 .client-line {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 11.5px;
    font-weight: 400;
    color: #374151;
  }
  .invoice-theme-cgm2 .blue-text,
  .invoice-theme-cgm2 a.email-link { color: #a88974 !important; font-weight: 500; }
  .invoice-theme-cgm2 .convention {
    margin-top: 8px;
    display: inline-block;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #5c4f45;
    background: rgba(150, 120, 100, 0.1);
    border: 1px solid rgba(140, 120, 105, 0.28);
    padding: 3px 10px;
    border-radius: 4px;
  }
  .invoice-theme-cgm2 .invoice-number {
    color: #5c4f45;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 16px;
    font-weight: 700 !important;
    margin: 0;
  }
  .invoice-theme-cgm2 .invoice-number .nh-label {
    display: block;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(92, 79, 69, 0.65);
    margin-bottom: 4px;
  }
  .invoice-theme-cgm2 .invoice-number .nh-ref {
    display: inline-block;
    font-size: 17px;
    font-weight: 700;
    color: #5c4f45;
    border-bottom: none !important;
    text-decoration: none !important;
    padding-bottom: 0;
  }
  .invoice-theme-cgm2 .nh-date {
    margin-top: 6px;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 11px;
    font-weight: 400;
    color: #4b5563;
  }
  .invoice-theme-cgm2 .invoice-table {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    border: 1px solid rgba(140, 120, 105, 0.18);
    background: rgba(255, 255, 255, 0.35);
  }
  .invoice-theme-cgm2 .invoice-table th {
    background: rgba(150, 120, 100, 0.28) !important;
    color: #4a4038 !important;
    border-bottom: none;
    font-size: 10px;
    font-weight: 600 !important;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .invoice-theme-cgm2 .invoice-table td { border-bottom: 1px solid rgba(140, 120, 105, 0.1); font-size: 11.5px; font-weight: 400 !important; }
  .invoice-theme-cgm2 .row-id { background-color: rgba(150, 120, 100, 0.26) !important; color: #4a4038 !important; font-weight: 600 !important; }
  .invoice-theme-cgm2 .designation,
  .invoice-theme-cgm2 .bg-light { background-color: rgba(245, 242, 239, 0.45) !important; }
  .invoice-theme-cgm2 .cell-tva { background-color: rgba(150, 120, 100, 0.12) !important; color: #5c4f45 !important; }
  .invoice-theme-cgm2 .bg-blue-highlight { background-color: rgba(150, 120, 100, 0.3) !important; color: #4a4038 !important; font-weight: 600 !important; }
  .invoice-theme-cgm2 .totals-grid {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    border: 1px solid rgba(140, 120, 105, 0.18);
    background: rgba(245, 242, 239, 0.4);
  }
  .invoice-theme-cgm2 .totals-grid .total-label { color: #374151; font-weight: 500 !important; }
  .invoice-theme-cgm2 .totals-grid .total-value { font-weight: 600 !important; color: #111827; }
  .invoice-theme-cgm2 .totals-grid .border-top {
    border-top: 2px solid rgba(150, 120, 100, 0.4) !important;
    color: #5c4f45 !important;
    font-weight: 700 !important;
    letter-spacing: 0.02em;
    text-transform: none;
  }
  .invoice-theme-cgm2 .thanks {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-style: normal;
    font-size: 14px;
    font-weight: 500;
    color: #5c4f45;
  }
  .invoice-theme-cgm2 .bottom-note-container {
    background: rgba(245, 242, 239, 0.45);
    border-left: 4px solid rgba(150, 120, 100, 0.35);
    border-radius: 0 6px 6px 0;
  }
  .invoice-theme-cgm2 .amount-words-label {
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(92, 79, 69, 0.7);
    margin-bottom: 3px;
    font-weight: 600;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
  }
  .invoice-theme-cgm2 .amount-in-words strong {
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
    font-size: 12.5px;
    font-weight: 700;
    color: #5c4f45;
  }
  .invoice-theme-cgm2 .footer-line {
    border-top: 1px solid rgba(140, 120, 105, 0.2);
    color: #6b7280;
    font-family: Calibri, 'Segoe UI', Arial, sans-serif;
  }
`;

export function buildInvoicePrintHtml(data, formatNumber) {
  const {
    logoUrl,
    cachetUrl,
    societe,
    adresse,
    matriculeFiscale,
    email,
    numero,
    date,
    designation,
    lignes,
    tvaPct,
    montantHT,
    montantTTC,
    montantTVA,
    grandTotal,
    timbreFiscal,
    retenueSource,
    netAPayer,
    montantEnLettres,
    matriculeComptable,
    natureContrat,
  } = data;

  const matKey = String(matriculeComptable || '').trim();
  const isCgm2 =
    data.theme === 'cgm2' ||
    matKey === '1835448D' ||
    matKey.toUpperCase().startsWith('1835448');
  const cabinetKey = isCgm2 ? '1835448D' : '1408941H';

  const CABINET_MF = {
    '1408941H': '1408941/H/A/P/000',
    '1835448D': '1835448/D/A/M/000',
  };
  const cabinetMf = CABINET_MF[cabinetKey];

  const CABINET_ADDR = {
    '1408941H': '16 Bis rue Youssef Rouissi, Borej Louzir, Soukra 2073, Ariana',
    '1835448D': '62 Avenue de France 2013 Ben Arous',
  };
  const cabinetAddr = CABINET_ADDR[cabinetKey];

  const CABINET_NAME = {
    '1408941H': 'MOHAMED GHOUFA "CGM"',
    '1835448D': 'Cabinet Ghoufa Mohamed CGM',
  };
  const cabinetName = CABINET_NAME[cabinetKey];

  const CABINET_EMAIL = {
    '1408941H': 'contact@ccgm.com.tn',
    '1835448D': 'comptabilite@ccgm.com.tn',
  };
  const cabinetEmail = CABINET_EMAIL[cabinetKey];

  const CABINET_RIB = {
    '1408941H': '25047000000039870129',
    '1835448D': '25047000000098929929',
  };
  const cabinetRib = CABINET_RIB[cabinetKey];

  const client = normalizeInvoiceClientData({
    societe,
    adresse,
    matriculeFiscale,
    email,
  });

  const fmt = (n) => escapeHtml(formatNumber(n));
  const safeSociete = escapeHtml(client.societe);
  const safeAdresse = escapeHtml(client.adresse);
  const safeMf = escapeHtml(client.matriculeFiscale);
  const safeEmail = escapeHtml(client.email);
  const safeNumero = escapeHtml(formatNhNumeroForPrint(numero));
  const safeLettres = escapeHtml(montantEnLettres || '');
  // Lignes de la note : multi-lignes si fourni, sinon une seule ligne (rétro-compatible).
  const invoiceLignes =
    Array.isArray(lignes) && lignes.length > 0
      ? lignes
      : [{ ordre: 1, designation, tauxTva: tvaPct, puHtv: montantHT, puTtc: montantTTC }];
  const lignesRowsHtml = invoiceLignes
    .map((l, i) => {
      const ht = parseFloat(String(l.puHtv ?? '0').replace(',', '.')) || 0;
      const taux = parseFloat(String(l.tauxTva ?? '0').replace(',', '.')) || 0;
      const ttc = l.puTtc != null ? parseFloat(String(l.puTtc).replace(',', '.')) || ht * (1 + taux / 100) : ht * (1 + taux / 100);
      return `        <tr>
          <td class="row-id">${l.ordre ?? i + 1}</td>
          <td class="designation">${escapeHtml(l.designation || '')}</td>
          <td class="cell-tva">${escapeHtml(String(taux))}%</td>
          <td class="bg-light">${fmt(ht)}</td>
          <td class="bg-blue-highlight">${fmt(ttc)}</td>
        </tr>`;
    })
    .join('\n');
  const printDateTime = escapeHtml(formatPrintDateTime(new Date()));

  const cabinetDetailsHtml = isCgm2
    ? `<div>MF : ${escapeHtml(cabinetMf)}</div>
            <div>Banque Zitouna — Agence Ben Arous</div>
            <div>RIB ${escapeHtml(cabinetRib)}</div>`
    : `<div>Matricule Fiscale : ${escapeHtml(cabinetMf)}</div>
            <div>Banque Zitouna Agence Ben Arous</div>
            <div>RIB : ${escapeHtml(cabinetRib)}</div>`;

  const subtitleHtml = isCgm2
    ? 'Expert-comptable — Compagnie des Comptables de Tunisie'
    : 'Membre de la Compagnie des Comptables de Tunisie';

  const metaHtml = isCgm2
    ? `<h2 class="invoice-number">
          <span class="nh-label">Note d'honoraires</span>
          <span class="nh-ref">${safeNumero}</span>
        </h2>
        <div class="nh-date">Émise le ${escapeHtml(formatInvoiceDate(date))}</div>`
    : `<h2 class="invoice-number">NOTE D'HONORAIRES ${safeNumero}</h2>
        <div>Date : ${escapeHtml(formatInvoiceDate(date))}</div>`;

  const tableHeadHtml = isCgm2
    ? `<th>N°</th>
          <th>Désignation</th>
          <th>TVA</th>
          <th>P.U. HT</th>
          <th>P.U. TTC</th>`
    : `<th>#</th>
          <th>DESIGNATION</th>
          <th>TAUX TVA</th>
          <th>PU HTVA</th>
          <th>PU TTC</th>`;

  const totalsHtml = isCgm2
    ? `<div class="total-label">Total HT</div><div class="total-value">${fmt(montantHT)}</div>
        <div class="total-label">Montant TVA</div><div class="total-value">${fmt(montantTVA)}</div>
        <div class="total-label">Total TTC</div><div class="total-value">${fmt(montantTTC)}</div>
        <div class="total-label">Timbre fiscal</div><div class="total-value">${fmt(timbreFiscal)}</div>
        <div class="total-label">Grand total</div><div class="total-value">${fmt(grandTotal)}</div>
        <div class="total-label rs-line">Retenue à la source</div><div class="total-value rs-line">${fmt(retenueSource)}</div>
        <div class="total-label border-top">Net à payer</div>
        <div class="total-value border-top large">${fmt(netAPayer)}</div>`
    : `<div class="total-label">TOTAL HT</div><div class="total-value">${fmt(montantHT)}</div>
        <div class="total-label">MONTANT TVA</div><div class="total-value">${fmt(montantTVA)}</div>
        <div class="total-label">TOTAL TTC</div><div class="total-value">${fmt(montantTTC)}</div>
        <div class="total-label">TIMBRE FISCAL</div><div class="total-value">${fmt(timbreFiscal)}</div>
        <div class="total-label">GRAND TOTAL</div><div class="total-value">${fmt(grandTotal)}</div>
        <div class="total-label rs-line">R/S</div><div class="total-value rs-line">${fmt(retenueSource)}</div>
        <div class="total-label border-top">NET À PAYER</div>
        <div class="total-value border-top large">${fmt(netAPayer)}</div>`;

  const amountWordsHtml = isCgm2
    ? `<div class="amount-words-label">Arrêtée la présente note d'honoraires à la somme de</div>
          <div><strong>${safeLettres}</strong></div>`
    : `<div>ARRÊTÉE LA PRÉSENTE NOTE D'HONORAIRE À LA SOMME DE :</div>
          <div><strong>${safeLettres}</strong></div>`;

  const thanksHtml = isCgm2 ? 'Avec nos remerciements.' : 'Merci !';
  const footerHtml = isCgm2
    ? 'Document établi informatiquement — valable sans signature ni cachet manuscrit.'
    : "La facture a été générée par un ordinateur compte tenu de certaines données et est valable sans la signature et le cachet de l'entreprise.";

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <title>Note d'honoraires</title>
  <style>${INVOICE_PRINT_STYLES}</style>
</head>
<body>
  <div class="invoice-paper${isCgm2 ? ' invoice-theme-cgm2' : ''}">
    <div class="invoice-print-banner">
      <div class="invoice-print-banner-left">${printDateTime}</div>
    </div>
    <div class="invoice-header">
      <div class="cabinet-logo">
        <div class="logo-placeholder-invoice">
          <img src="${escapeHtml(logoUrl)}" alt="${isCgm2 ? 'Logo CGM2' : 'Logo CGM'}" class="header-logo-img" />
          <div class="cabinet-details">
            ${cabinetDetailsHtml}
          </div>
        </div>
      </div>
      <div class="cabinet-name">
        <h1>${escapeHtml(cabinetName)}</h1>
        <p class="subtitle">${escapeHtml(subtitleHtml)}</p>
        <div class="contact-info">
          <div>${escapeHtml(cabinetAddr)}</div>
          <div>(+216) 79 410 028</div>
          <div>${escapeHtml(cabinetEmail)}</div>
        </div>
      </div>
    </div>
    <div class="blue-divider"></div>
    <div class="client-section">
      <div class="client-details">
        <h2>${safeSociete}</h2>
        ${safeAdresse ? `<div class="client-line">${safeAdresse}</div>` : ''}
        ${safeMf ? `<div class="client-line blue-text">${safeMf}</div>` : ''}
        ${safeEmail ? `<div class="client-line blue-text"><a class="email-link" href="mailto:${safeEmail}">${safeEmail}</a></div>` : ''}
        <p class="convention"><strong>${escapeHtml(natureContrat || 'CONVENTION')}</strong></p>
      </div>
      <div class="invoice-meta">
        ${metaHtml}
      </div>
    </div>
    <table class="invoice-table">
      <thead>
        <tr>
          ${tableHeadHtml}
        </tr>
      </thead>
      <tbody>
${lignesRowsHtml}
      </tbody>
    </table>
    <div class="totals-section">
      <div class="totals-grid">
        ${totalsHtml}
      </div>
    </div>
    <div class="invoice-footer-block">
      <p class="thanks">${thanksHtml}</p>
      <div class="bottom-note-container">
        <div class="amount-in-words">
          ${amountWordsHtml}
        </div>
        <div class="stamp-placeholder">
          <img src="${escapeHtml(cachetUrl)}" alt="${isCgm2 ? 'Cachet CGM2' : 'Cachet CGM'}" class="cachet-img" />
        </div>
      </div>
      <div class="footer-line">
        ${escapeHtml(footerHtml)}
      </div>
    </div>
  </div>
</body>
</html>`;
}

function waitForPrintImages(doc) {
  const images = Array.from(doc.images || []);
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
}

/** Charge le HTML facture dans une iframe (même rendu que l'impression). */
async function loadInvoiceHtmlInIframe(html, options = {}) {
  const {
    iframeId = 'invoice-render-iframe',
    title = 'Facture',
    widthPx = 900,
    hidden = false,
  } = options;

  const prev = document.getElementById(iframeId);
  if (prev) prev.remove();

  const iframe = document.createElement('iframe');
  iframe.id = iframeId;
  iframe.setAttribute('title', title);
  iframe.style.cssText = hidden
    ? 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;top:0;'
    : `position:fixed;left:-9999px;top:0;width:${widthPx}px;border:0;background:#fff;`;
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  if (!win) {
    throw new Error('Impossible de créer le document facture');
  }

  const doc = win.document;
  doc.open();
  doc.write(html);
  doc.close();

  await new Promise((resolve) => {
    if (doc.readyState === 'complete') resolve();
    else iframe.onload = () => resolve();
  });

  await waitForPrintImages(doc);
  await new Promise((r) => setTimeout(r, 150));

  const paper = doc.querySelector('.invoice-paper');
  if (!paper) {
    iframe.remove();
    throw new Error('Document facture introuvable');
  }

  if (!hidden) {
    const h = Math.max(paper.scrollHeight, paper.offsetHeight, 400) + 24;
    iframe.style.height = `${h}px`;
  }

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  return { iframe, doc, paper, cleanup };
}

async function savePdfToServer(pdfBlob, fileName, subfolder) {
  const formData = new FormData();
  formData.append('pdf', pdfBlob, fileName);
  formData.append('fileName', fileName);
  if (subfolder) formData.append('subfolder', subfolder);

  const res = await fetch('/api/nh-notes/save-pdf', {
    method: 'POST',
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.error || 'Erreur serveur');
  }
  return data;
}

/** Téléchargement local : boîte « Enregistrer sous » si possible, sinon téléchargement navigateur. */
async function downloadPdfBlob(pdfBlob, fileName, { promptSaveLocation = true } = {}) {
  const safeName = (fileName || 'Note_Honoraires.pdf').replace(/[\\/:*?"<>|]/g, '_').trim() || 'Note_Honoraires.pdf';

  if (promptSaveLocation && typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(pdfBlob);
      await writable.close();
      return { downloaded: true, cancelled: false, method: 'picker' };
    } catch (err) {
      if (err?.name === 'AbortError') {
        return { downloaded: false, cancelled: true, method: 'picker' };
      }
      console.warn('Enregistrement via sélecteur de fichier impossible:', err);
    }
  }

  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return { downloaded: true, cancelled: false, method: 'download' };
}

/** Export PDF — même HTML/CSS que l'impression (buildInvoicePrintHtml). */
export async function exportInvoicePdfDocument(
  data,
  formatNumber,
  fileName = 'Note_Honoraires.pdf',
  options = {}
) {
  const html = buildInvoicePrintHtml(data, formatNumber);
  const { paper, cleanup } = await loadInvoiceHtmlInIframe(html, {
    iframeId: 'invoice-pdf-iframe',
    title: 'Export PDF facture',
    widthPx: 900,
    hidden: false,
  });

  try {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    // scale 1.5 + JPEG : PDF beaucoup plus léger (~0.3–1.5 Mo) tout en restant lisible
    const canvas = await html2canvas(paper, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      onclone: (clonedDoc) => {
        clonedDoc.querySelectorAll('.invoice-table th, .invoice-table td').forEach((el) => {
          el.style.verticalAlign = 'middle';
          el.style.paddingTop = '10px';
          el.style.paddingBottom = '10px';
        });
        clonedDoc.querySelectorAll('.totals-grid .total-label, .totals-grid .total-value').forEach((el) => {
          el.style.display = 'flex';
          el.style.alignItems = 'center';
          el.style.justifyContent = 'flex-end';
          el.style.minHeight = '34px';
          el.style.boxSizing = 'border-box';
        });
        // Forcer le thème orange-gris transparent CGM2 pour html2canvas.
        const paper = clonedDoc.querySelector('.invoice-theme-cgm2');
        if (paper) {
          paper.style.webkitPrintColorAdjust = 'exact';
          paper.style.printColorAdjust = 'exact';
          paper.style.backgroundColor = 'rgba(245, 242, 239, 0.65)';
          paper.style.border = '1px solid rgba(140, 120, 105, 0.2)';
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .invoice-table th').forEach((el) => {
            el.style.backgroundColor = 'rgba(150, 120, 100, 0.28)';
            el.style.color = '#4a4038';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .row-id').forEach((el) => {
            el.style.backgroundColor = 'rgba(150, 120, 100, 0.26)';
            el.style.color = '#4a4038';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .bg-blue-highlight').forEach((el) => {
            el.style.backgroundColor = 'rgba(150, 120, 100, 0.3)';
            el.style.color = '#4a4038';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .designation, .invoice-theme-cgm2 .bg-light').forEach((el) => {
            el.style.backgroundColor = 'rgba(245, 242, 239, 0.45)';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .cell-tva').forEach((el) => {
            el.style.backgroundColor = 'rgba(150, 120, 100, 0.12)';
            el.style.color = '#5c4f45';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .totals-grid').forEach((el) => {
            el.style.backgroundColor = 'rgba(245, 242, 239, 0.4)';
            el.style.border = '1px solid rgba(140, 120, 105, 0.18)';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .bottom-note-container').forEach((el) => {
            el.style.backgroundColor = 'rgba(245, 242, 239, 0.45)';
            el.style.borderLeft = '4px solid rgba(150, 120, 100, 0.35)';
          });
          clonedDoc.querySelectorAll('.invoice-theme-cgm2 .blue-text, .invoice-theme-cgm2 .border-top').forEach((el) => {
            el.style.color = '#5c4f45';
          });
        }
      },
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL('image/jpeg', 0.78);

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const pdfBlob = pdf.output('blob');

    let serverSaved = false;
    let serverResult = null;
    let serverError = null;
    let downloadResult = { downloaded: false, cancelled: false, method: null };

    if (options.saveToServer === true) {
      try {
        serverResult = await savePdfToServer(pdfBlob, fileName, options.subfolder || '');
        serverSaved = true;
      } catch (serverErr) {
        serverError = serverErr.message || String(serverErr);
        console.warn('Export PDF serveur échoué:', serverErr);
      }
    }

    if (options.skipDownload !== true) {
      downloadResult = await downloadPdfBlob(pdfBlob, fileName, {
        promptSaveLocation: options.promptSaveLocation !== false,
      });
    }

    return { serverSaved, serverResult, serverError, ...downloadResult };
  } finally {
    cleanup();
  }
}

/** Impression via iframe cachée — dialogue d'impression direct, sans pop-up. */
export function printInvoiceDocument(data, formatNumber, title = 'Note honoraires') {
  const html = buildInvoicePrintHtml(data, formatNumber);

  const prev = document.getElementById('invoice-print-iframe');
  if (prev) prev.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'invoice-print-iframe';
  iframe.setAttribute('title', title || 'Impression facture');
  iframe.style.cssText =
    'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;left:-9999px;top:0;';
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  if (!win) return;

  const doc = win.document;
  doc.open();
  doc.write(html);
  doc.close();

  const runPrint = () => {
    try {
      doc.title = title || 'Note honoraires';
    } catch {
      /* ignore */
    }

    const cleanup = () => {
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 500);
    };

    try {
      win.focus();
      win.onafterprint = cleanup;
      win.print();
    } catch (err) {
      console.error('Impression facture:', err);
      cleanup();
    }

    setTimeout(cleanup, 120000);
  };

  const start = () => {
    waitForPrintImages(doc).then(() => {
      setTimeout(runPrint, 150);
    });
  };

  if (doc.readyState === 'complete') {
    start();
  } else {
    iframe.onload = start;
  }
}

// ========== Auto-export PDF depuis une note (appelé après ajout/duplication) ==========

function parseTauxPercent(tauxTaxe) {
  const s = String(tauxTaxe ?? '0');
  const m = s.match(/(\d+(?:[.,]\d+)?)/);
  return m ? parseFloat(m[1].replace(',', '.')) || 0 : 0;
}

function autoComputeTotals(note) {
  const ht = parseFloat(String(note.montantHT ?? '0').replace(',', '.')) || 0;
  const tvaPct = parseTauxPercent(note.tauxTaxe);
  const retenueTaux = parseFloat(note.fiscalParams?.retenue ?? 3) || 0;
  const timbre = parseFloat(String(note.fiscalParams?.timbre ?? 1).replace(',', '.')) || 0;

  const montantTTC = ht * (1 + tvaPct / 100);
  const montantTVA = montantTTC - ht;
  const grandTotal = montantTTC + timbre;
  const retenueSource = montantTTC * (retenueTaux / 100);
  const netAPayer = grandTotal - retenueSource;

  return { montantHT: ht, montantTTC, montantTVA, grandTotal, retenueSource, netAPayer, timbre, retenueTaux, tvaPct };
}

function autoAmountToWords(montant) {
  const unite = ['', 'UN', 'DEUX', 'TROIS', 'QUATRE', 'CINQ', 'SIX', 'SEPT', 'HUIT', 'NEUF',
    'DIX', 'ONZE', 'DOUZE', 'TREIZE', 'QUATORZE', 'QUINZE', 'SEIZE', 'DIX-SEPT', 'DIX-HUIT', 'DIX-NEUF'];
  const dizaine = ['', 'DIX', 'VINGT', 'TRENTE', 'QUARANTE', 'CINQUANTE', 'SOIXANTE',
    'SOIXANTE-DIX', 'QUATRE-VINGT', 'QUATRE-VINGT-DIX'];
  const convertDiz = (n) => {
    if (n < 20) return unite[n];
    let d = Math.floor(n / 10), u = n % 10;
    if (d === 7 || d === 9) return dizaine[d] + (u === 1 ? '-ET-' : '-') + unite[u + 10];
    if (u === 0) return dizaine[d];
    if (u === 1) return dizaine[d] + '-ET-UN';
    return dizaine[d] + '-' + unite[u];
  };
  const convertCent = (n) => {
    if (n < 100) return convertDiz(n);
    let c = Math.floor(n / 100), r = n % 100;
    let cent = c === 1 ? 'CENT' : unite[c] + '-CENT';
    if (r === 0) return cent + (c > 1 ? 'S' : '');
    return cent + '-' + convertDiz(r);
  };
  const numWords = (n) => {
    n = Math.floor(n);
    if (n === 0) return 'ZÉRO';
    if (n < 1000) return convertCent(n);
    let mil = Math.floor(n / 1000), rest = n % 1000;
    let res = mil === 1 ? 'MILLE' : convertCent(mil) + ' MILLE';
    if (rest > 0) res += ' ' + convertCent(rest);
    return res;
  };
  const ent = Math.floor(montant), dec = Math.round((montant - ent) * 1000);
  let res = numWords(ent) + (ent > 1 ? ' DINARS' : ' DINAR');
  if (dec > 0) res += ' ET ' + numWords(dec) + (dec > 1 ? ' MILLIMES' : ' MILLIME');
  return res;
}

const AUTO_FORMAT_NUMBER = (n) => n.toFixed(3).replace('.', ',');

/**
 * Ancien export auto serveur — désactivé : utiliser « Exporter PDF » sur la facture.
 */
export async function autoExportNotePdf() {
  return;
}
